import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Shield, ArrowLeft } from 'lucide-react';

const PrivacyPolicy = () => {
  return (
    <div className="legal-page" data-testid="privacy-policy-page">
      <Helmet>
        <title>Privacy Policy | The Gas Guide</title>
        <meta
          name="description"
          content="How The Gas Guide collects, uses and protects your personal information. UK GDPR & Data Protection Act 2018 compliant."
        />
      </Helmet>

      {/* Hero */}
      <section className="legal-hero">
        <div className="legal-hero-content">
          <Link to="/" className="legal-back-link" data-testid="privacy-back-link">
            <ArrowLeft size={16} /> Back to Home
          </Link>
          <span className="legal-label">
            <Shield size={14} /> Legal
          </span>
          <h1 className="legal-headline">Privacy Policy</h1>
          <p className="legal-subline">
            How we collect, use, and protect your information.
          </p>
          <p className="legal-meta">
            Effective date: <strong>2 March 2026</strong> &nbsp;·&nbsp; Last updated: 2 March 2026
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="legal-body">
        <div className="legal-container">
          <div className="legal-intro">
            <p>
              This Privacy Policy explains how <strong>The Gas Guide</strong> ("we", "us", "our")
              collects, uses, stores and shares personal information when you use our website
              <a href="https://www.thegasguide.co.uk"> www.thegasguide.co.uk</a> (the "Service").
              We are committed to protecting your privacy and complying with the UK General Data
              Protection Regulation (UK GDPR) and the Data Protection Act 2018.
            </p>
            <p>
              The Gas Guide operates as a UK directory connecting homeowners with Gas Safe
              registered heating engineers. By using our Service, you agree to the practices
              described in this policy.
            </p>
          </div>

          <h2>1. Who we are</h2>
          <p>
            The Gas Guide is a trading name operating an online directory and lead-generation
            platform for Gas Safe registered engineers across the United Kingdom. For any
            privacy-related queries, you can contact us at{' '}
            <a href="mailto:support@thegasguide.co.uk">support@thegasguide.co.uk</a>.
          </p>

          <h2>2. Information we collect</h2>
          <p>We collect different categories of information depending on how you use the Service.</p>

          <h3>2.1 Information from homeowners / customers</h3>
          <ul>
            <li>
              <strong>Postcode searches:</strong> When you search for an engineer, we record the
              postcode you entered, the time of search, and the number of results returned.
            </li>
            <li>
              <strong>Review submissions:</strong> Your name (or display name), email address,
              rating, and the text of any review you submit.
            </li>
            <li>
              <strong>Contact form data:</strong> Name, email address and message content if you
              contact us directly.
            </li>
            <li>
              <strong>Technical data:</strong> IP address, browser type, device information and
              approximate location derived from your postcode lookup.
            </li>
          </ul>

          <h3>2.2 Information from engineers</h3>
          <ul>
            <li>
              <strong>Identification &amp; trade data:</strong> Full name, business name, Gas Safe
              registration number, ID category, postcode coverage area.
            </li>
            <li>
              <strong>Contact data:</strong> Email address, phone number, business address.
            </li>
            <li>
              <strong>Marketing assets:</strong> Photos of completed work, profile photos and
              logos uploaded to your listing.
            </li>
            <li>
              <strong>Account &amp; payment data:</strong> Login credentials (passwords are stored
              hashed), subscription status, trial dates, and Stripe customer/subscription IDs.
              We do not store full card details — these are handled directly by Stripe.
            </li>
            <li>
              <strong>Activity data:</strong> Login timestamps, profile updates, badge downloads
              and email open events.
            </li>
          </ul>

          <h2>3. How we use your information</h2>
          <p>We process your personal data for the following purposes:</p>
          <ul>
            <li>To operate the directory and match homeowners with relevant engineers.</li>
            <li>To verify Gas Safe registration and protect users from unvetted tradespeople.</li>
            <li>To process subscriptions, trials and renewals via Stripe.</li>
            <li>
              To send transactional emails (account creation, trial reminders, password resets,
              renewal notifications).
            </li>
            <li>
              To send service-related communications and, where lawful, marketing emails. You can
              opt out at any time.
            </li>
            <li>
              To monitor demand by area (aggregated postcode searches) so we can recruit
              engineers in underserved regions.
            </li>
            <li>To prevent fraud, enforce our Terms of Service, and comply with legal duties.</li>
          </ul>

          <h2>4. Lawful bases for processing</h2>
          <p>Under UK GDPR we rely on the following lawful bases:</p>
          <ul>
            <li>
              <strong>Contract:</strong> to deliver the Service to engineers and homeowners.
            </li>
            <li>
              <strong>Legitimate interests:</strong> to maintain platform safety, prevent fraud,
              measure service demand, and improve our directory.
            </li>
            <li>
              <strong>Consent:</strong> for non-essential cookies, marketing emails, and review
              publication.
            </li>
            <li>
              <strong>Legal obligation:</strong> where we are required to retain or disclose
              information by law.
            </li>
          </ul>

          <h2>5. Cookies &amp; tracking technologies</h2>
          <p>
            We use a small number of cookies and similar technologies to operate the Service and
            understand how it is used. These include:
          </p>
          <ul>
            <li>
              <strong>Strictly necessary cookies:</strong> session tokens that keep engineers and
              admins logged in to their portals.
            </li>
            <li>
              <strong>Functional storage:</strong> local storage entries that remember your
              postcode preferences for faster searching.
            </li>
            <li>
              <strong>Search analytics:</strong> we log every postcode entered into our search bar
              (along with the timestamp and number of matches). This is used internally to
              identify areas where we need to recruit more engineers and is never sold.
            </li>
            <li>
              <strong>Email read receipts:</strong> emails sent from our admin portal may include
              a 1×1 transparent tracking pixel that records whether and when the email was
              opened. This helps us confirm delivery of trial reminders and important account
              notices. You can block remote images in your email client to prevent this.
            </li>
          </ul>
          <p>
            We do not currently use third-party advertising cookies. You can control cookies via
            your browser settings; disabling strictly necessary cookies will prevent the
            engineer/admin portals from functioning.
          </p>

          <h2>6. Sharing your information</h2>
          <p>We do not sell your personal data. We share data only with:</p>
          <ul>
            <li>
              <strong>Stripe</strong> — to process subscription payments. See Stripe's privacy
              notice at{' '}
              <a href="https://stripe.com/gb/privacy" target="_blank" rel="noopener noreferrer">
                stripe.com/gb/privacy
              </a>
              .
            </li>
            <li>
              <strong>Google Workspace</strong> — to deliver transactional and marketing emails
              from <em>support@thegasguide.co.uk</em>.
            </li>
            <li>
              <strong>postcodes.io</strong> — a public UK postcode lookup service used to derive
              latitude/longitude for map and distance calculations.
            </li>
            <li>
              <strong>Map providers (OpenStreetMap / Leaflet)</strong> — to render search and
              admin maps.
            </li>
            <li>
              <strong>Gas Safe Register</strong> — we cross-reference the publicly available Gas
              Safe Register to verify engineer credentials.
            </li>
            <li>
              <strong>Law enforcement / regulators</strong> — where we are legally required to
              disclose data.
            </li>
          </ul>

          <h2>7. International transfers</h2>
          <p>
            Some of our service providers (e.g. Stripe, Google) may process data outside the UK.
            Where this happens, we rely on UK GDPR-approved transfer mechanisms such as the UK
            International Data Transfer Agreement or Standard Contractual Clauses.
          </p>

          <h2>8. Data retention</h2>
          <ul>
            <li>
              <strong>Engineer accounts:</strong> kept while the account is active and for up to
              6 years after closure to meet UK accounting and tax requirements.
            </li>
            <li>
              <strong>Customer reviews:</strong> kept for as long as the engineer's listing
              remains live.
            </li>
            <li>
              <strong>Postcode search logs:</strong> kept in aggregated form for service
              improvement; raw entries are typically deleted after 24 months.
            </li>
            <li>
              <strong>Email tracking events:</strong> kept for up to 12 months.
            </li>
            <li>
              <strong>Contact form messages:</strong> kept for up to 24 months unless an ongoing
              matter requires longer retention.
            </li>
          </ul>

          <h2>9. Your rights</h2>
          <p>Under UK GDPR you have the right to:</p>
          <ul>
            <li>Request access to the personal data we hold about you.</li>
            <li>Request correction of inaccurate or incomplete data.</li>
            <li>Request deletion of your data ("right to be forgotten").</li>
            <li>Restrict or object to certain processing activities.</li>
            <li>Request a copy of your data in a portable format.</li>
            <li>Withdraw consent at any time where we rely on it.</li>
            <li>
              Lodge a complaint with the Information Commissioner's Office (ICO) at{' '}
              <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer">
                ico.org.uk
              </a>
              .
            </li>
          </ul>
          <p>
            To exercise any of these rights, email{' '}
            <a href="mailto:support@thegasguide.co.uk">support@thegasguide.co.uk</a>. We will
            respond within one calendar month.
          </p>

          <h2>10. Security</h2>
          <p>
            We take reasonable technical and organisational measures to protect your data,
            including TLS/HTTPS encryption in transit, hashed passwords, restricted admin access,
            and use of trusted infrastructure providers. No system is 100% secure, and we cannot
            guarantee absolute security.
          </p>

          <h2>11. Children</h2>
          <p>
            The Gas Guide is not intended for use by children under the age of 16. We do not
            knowingly collect data from children.
          </p>

          <h2>12. Changes to this policy</h2>
          <p>
            We may update this Privacy Policy from time to time. The "Last updated" date at the
            top will reflect any changes. Material changes will be communicated via email or a
            prominent notice on the Service.
          </p>

          <h2>13. Contact us</h2>
          <p>
            If you have any questions about this Privacy Policy or how we handle your personal
            data, please contact:
          </p>
          <p className="legal-contact-block">
            <strong>The Gas Guide</strong>
            <br />
            Email: <a href="mailto:support@thegasguide.co.uk">support@thegasguide.co.uk</a>
            <br />
            Website:{' '}
            <a href="https://www.thegasguide.co.uk" target="_blank" rel="noopener noreferrer">
              www.thegasguide.co.uk
            </a>
          </p>

          <div className="legal-footer-links">
            <Link to="/terms" data-testid="privacy-to-terms-link">
              Read our Terms of Service →
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
